module AppHost
open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop
open Fulma
open IFrameHost

type Props = {
    appUrl : string
    cmsUrl : string
    openIam : (string -> unit) option
    showOpenInNewTab : bool
    langs : string
}

type private State =
| Initializing 
| Working of string
| Error of string

type private Msg =
| TokenRecieved of string
| TokenError of string

let private init =
    Initializing

let private update state = function
    | TokenRecieved token -> Working token
    | TokenError err -> Error err

let private getToken url dispatch () =
    promise {
        try 
            let! token = Service.getToken url
            token.token |> TokenRecieved |> dispatch
        with 
            | e -> e.ToString() |> TokenError |> dispatch
    } |> ignore

let private appEditorHost_fComp =
    FunctionComponent.Of
        (fun props ->
            let state = Hooks.useReducer(update, init)

            Hooks.useEffect(
                (getToken
                    props.appUrl
                    state.update),
                [|props.appUrl|])

            match state.current with
            | Initializing -> str "Loading..."
            | Error e -> str e
            | Working token ->
                div [ ClassName "stretch-height" ]
                    [
                        if props.showOpenInNewTab
                        then                        
                            Fulma.Notification.notification
                                [Fulma.Notification.Color (Color.IsWarning) ]
                                [str "Application CMS (";
                                 str props.cmsUrl;
                                 str ") uses unsecure protocol. It may not display correctly. Fix infrastructure or open this page over http as temporary workaround. ";
                                 a [ Href (props.cmsUrl + "?token=" + token + "&langs=" + props.langs); Target "_blank" ] [ str "Open editor in new tab" ]]
                        
                        iframeHost2
                            props.cmsUrl
                            token
                            props.langs
                            (props.openIam |> Option.map (fun handler -> fun (m,o) -> if m = "openIamEditor" then handler (o?objectId)))
                    ]
        )

type private WrapperProps = {
    forwardProps : Props
    iamUrl : string 
}

type private IamWindowProps = {
    iamUrl : string
    iamId : string
    langs : string
}

let private iamWindow_comp =
    FunctionComponent.Of
        (fun (props : IamWindowProps) ->
            match IFrameHost.addQueryParam props.iamUrl "objId" props.iamId with
            | Result.Error e -> str e
            | Result.Ok url ->
                appEditorHost_fComp {
                    appUrl = props.iamUrl 
                    cmsUrl = url
                    openIam = None
                    showOpenInNewTab = false
                    langs = props.langs
                }
            )

let iamWindow iamUrl iamId langs =
    iamWindow_comp {
        iamUrl = iamUrl
        iamId = iamId
        langs = langs
    }

let private wrapper_fComp =
    FunctionComponent.Of
        (fun (props:WrapperProps) ->
            let iamOpen = Hooks.useState<string option>(None)

            let childProps = { props.forwardProps with openIam = Some (Some >> iamOpen.update) }

            let app = 
                div [ Style [ Width ("100%"); Height("100%") ] ]
                    [ appEditorHost_fComp childProps ]

            let iam =
                match iamOpen.current with
                | Some id ->
                    Modal.modal
                        [ Modal.IsActive true; Modal.CustomClass "iam-container" ]
                        [ Modal.background [ Props [ OnClick (fun _ -> iamOpen.update None)] ] []
                          Modal.content [] [ Box.box' [] [ iamWindow props.iamUrl id childProps.langs ]] ]
                | None -> nothing

            div [ Class "plugin-frame-container" ] [ app; iam ]
        )

let editorWithIam appUrl cmsUrl langs iamUrl =
    wrapper_fComp {
        iamUrl = iamUrl
        forwardProps = {
            appUrl = appUrl
            cmsUrl = cmsUrl
            openIam = None
            showOpenInNewTab = false
            langs = langs
        }
    }

let editor appUrl cmsUrl showOpenInNewTab langs =
    appEditorHost_fComp {
        appUrl = appUrl
        cmsUrl = cmsUrl
        openIam = None
        showOpenInNewTab = showOpenInNewTab
        langs = langs
    }
